// src/services/dashboard/OrderService.js
import ApiFunctions from "./ApiFunctions";
import axios from "axios";

class OrderService extends ApiFunctions {
  constructor() {
    super("user-orders"); // استخدام نفس endpoint للطلبات الحقيقية
  }

  // Override getById لجلب طلب محدد بشكل صحيح
  getById = async (id, { withAuth = true, useCredentials = false } = {}) => {
    console.log(`🔍 Looking for order ID: ${id}`);
    
    try {
      // جلب جميع الطلبات والبحث عن المطلوب
      const allOrdersResponse = await this.get({ withAuth, useCredentials });
      
      console.log("📦 All Orders Response:", allOrdersResponse);
      
      if (allOrdersResponse.data && allOrdersResponse.data.data && Array.isArray(allOrdersResponse.data.data)) {
        console.log("📋 Available orders:", allOrdersResponse.data.data.map(o => ({
          id: o.id, 
          order_number: o.order_number,
          user_name: o.user?.name,
          total_amount: o.total_amount
        })));
        
        console.log("🔍 Full response structure:", allOrdersResponse.data);
        console.log("📊 Total orders in page 1:", allOrdersResponse.data.data.length);
        console.log("📄 Pagination info:", {
          current_page: allOrdersResponse.data.current_page,
          last_page: allOrdersResponse.data.last_page,
          total: allOrdersResponse.data.total
        });
        
        const targetOrder = allOrdersResponse.data.data.find(order => order.id == id);
        
        if (targetOrder) {
          console.log("✅ Found target order:", targetOrder);
          return {
            data: {
              data: targetOrder
            }
          };
        } else {
          console.log(`❌ Order ${id} not found in page 1. Available IDs:`, allOrdersResponse.data.data.map(o => o.id));
        }
      } else {
        console.log("❌ No orders data found or invalid structure:", allOrdersResponse.data);
      }
      
      // إذا لم نجد الطلب في الصفحة الأولى، جرب الصفحات التالية
      if (allOrdersResponse.data && allOrdersResponse.data.last_page > 1) {
        console.log(`🔄 Searching in ${allOrdersResponse.data.last_page} pages...`);
        
        for (let page = 2; page <= allOrdersResponse.data.last_page; page++) {
          console.log(`📄 Checking page ${page}...`);
          const pageResponse = await this.getWithPagination(page, "", { withAuth, useCredentials });
          
          if (pageResponse.data && pageResponse.data.data && Array.isArray(pageResponse.data.data)) {
            const targetOrder = pageResponse.data.data.find(order => order.id == id);
            
            if (targetOrder) {
              console.log(`✅ Found target order in page ${page}:`, targetOrder);
              return {
                data: {
                  data: targetOrder
                }
              };
            }
          }
        }
      }
      
      // إذا لم نجد الطلب، ارمي خطأ - لا بيانات افتراضية
      console.log(`❌ Order ${id} not found in any page`);
      throw new Error(`الطلب رقم ${id} غير موجود`);
      
    } catch (error) {
      console.error("❌ Error in getById:", error);
      // لا بيانات افتراضية - فقط بيانات حقيقية
      throw error;
    }
  };

  // Update order status
  updateStatus = async (id, status, orderData = null) => {
    if (!orderData) {
      throw new Error("بيانات الطلب مطلوبة للتحديث");
    }

    // جهز البيانات بالصيغة الصحيحة المطلوبة من الباك إند
    const updatedOrder = {
      order_number: orderData.order_number,
      user_id: orderData.user_id,
      total_amount: orderData.total_amount,
      status: status,
      order_items: orderData.order_items || [],
    };

    // نظف البيانات من undefined values
    Object.keys(updatedOrder).forEach(key => {
      if (updatedOrder[key] === undefined || updatedOrder[key] === null) {
        delete updatedOrder[key];
      }
    });

    console.log("إرسال البيانات المحدثة:", updatedOrder);

    // قائمة endpoints للتجربة لتحديث الطلبات
    const updateEndpoints = [
      `https://myappapi.fikriti.com/api/v1/dashboard/orders/${id}`,
      `https://myappapi.fikriti.com/api/v1/orders/${id}`,
      `https://myappapi.fikriti.com/api/v1/admin/orders/${id}`,
      `https://myappapi.fikriti.com/api/v1/update-order/${id}`,
      `https://myappapi.fikriti.com/api/v1/order-status/${id}`
    ];

    const headers = this.getHeaders({ withAuth: true });

    // جرب كل endpoint حتى يجد واحد يعمل
    for (let i = 0; i < updateEndpoints.length; i++) {
      const endpoint = updateEndpoints[i];
      console.log(`🔄 جرب update endpoint ${i + 1}/${updateEndpoints.length}: ${endpoint}`);
      
      try {
        // جرب PATCH أولاً
        const response = await axios.patch(endpoint, updatedOrder, headers);
        console.log(`✅ نجح PATCH على endpoint ${i + 1}: ${endpoint}`);
        return response;
        
      } catch (patchError) {
        console.warn(`❌ فشل PATCH على ${endpoint}:`, patchError.response?.status);
        
        // إذا فشل PATCH، جرب PUT
        try {
          const response = await axios.put(endpoint, updatedOrder, headers);
          console.log(`✅ نجح PUT على endpoint ${i + 1}: ${endpoint}`);
          return response;
          
        } catch (putError) {
          console.warn(`❌ فشل PUT على ${endpoint}:`, putError.response?.status);
          
          // إذا فشل PUT، جرب POST
          try {
            const response = await axios.post(endpoint, updatedOrder, headers);
            console.log(`✅ نجح POST على endpoint ${i + 1}: ${endpoint}`);
            return response;
            
          } catch (postError) {
            console.warn(`❌ فشل POST على ${endpoint}:`, postError.response?.status);
            
            // إذا كان آخر endpoint، احفظ الخطأ
            if (i === updateEndpoints.length - 1) {
              throw patchError; // ارمي الخطأ الأصلي
            }
          }
        }
      }
    }

    try {
      // إذا فشلت جميع المحاولات، جرب الطريقة الأصلية كـ fallback
      return await this.patch(id, updatedOrder, { withAuth: true });
    } catch (error) {
      console.log("خطأ في التحديث:", error.response?.status, error.response?.data);

      if (error.response?.status === 422) {
        console.log("تفاصيل خطأ 422 بالكامل:", JSON.stringify(error.response.data, null, 2));

        const errorMessage = error.response.data?.errors
          ? Object.entries(error.response.data.errors).map(([field, messages]) => `${field}: ${messages.join(', ')}`).join('\n')
          : error.response.data?.message || 'خطأ في البيانات المرسلة';

        console.log("الأخطاء المفصلة:", errorMessage);

        // جرب قيم مختلفة للحالة إذا كانت المشكلة في الحالة
        if (errorMessage.includes("حالة الطلب غير صحيحة")) {
          console.log("جرب قيم مختلفة للحالة...");
          // جرب قيم مختلفة قد تكون متوقعة من الباك إند
          const statusMapping = {
            'pending': 'pending',
            'processing': 'processing',
            'shipped': 'shipped',
            'completed': 'completed',
            'cancelled': 'cancelled'
          };

          if (statusMapping[status] && statusMapping[status] !== status) {
            console.log(`جرب ${statusMapping[status]} بدلاً من ${status}`);
            try {
              return await this.patch(id, {...updatedOrder, status: statusMapping[status]}, { withAuth: true });
            } catch (error2) {
              console.log("فشلت المحاولة بالقيمة البديلة");
            }
          }
        }

        alert(`خطأ في البيانات:\n${errorMessage}`);
      }

      throw error;
    }
  };
}

export default new OrderService(); // بنصدر instance واحدة مباشرة
